// Minimal Sentry event shape — avoids importing @sentry/core as a package dep
interface ISentryEventFrame {
  filename?: string;
  abs_path?: string;
}

interface ISentryEvent {
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: {
        frames?: ISentryEventFrame[];
      };
    }>;
  };
}

export const sentryIgnoreErrors: (string | RegExp)[] = [
  '$crisp is not defined',
  'jQuery is not defined',
  "Can't find variable: $crisp",
  "Can't find variable: jQuery",
  'Request failed with status code 401',
  'Request failed with status code 404',
  'Request failed with status code 422',
  "Can't find variable: fbq",
  'dataLayer is not defined',
  'fbq is not defined',
  "Can't find variable: ChartBoost",
  // Network errors — user offline or connectivity issue, not actionable
  'Network Error',
  /AxiosError: Network Error/,
  // Chunk load failures — stale tab after a deployment, not actionable
  /Failed to fetch dynamically imported module/,
  /Unable to preload CSS for/,
  /Loading chunk \d+ failed/,
  // Browser quirk, not a real error
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Intentional cancellation — user navigated away before the request finished
  /AxiosError: Request aborted/,
  // Request timeouts — not actionable from the frontend
  /AxiosError: timeout exceeded/,
  // Third-party: WordPress Popup Maker injected by tenant pages
  "Can't find variable: PUM",
  // Third-party: iOS WKWebView bridge probe from external scripts
  /window\.webkit\.messageHandlers/,
  // Third-party: browser extension trying to message a closed tab
  /Invalid call to runtime\.sendMessage/,
  // Third-party: Google Analytics param access from GTM scripts
  /storedParams\.ga_cid/,
  // Raw browser Event objects thrown as exceptions — always third-party scripts
  /Event `\w+` \(type=\w+\) captured as (exception|promise rejection)/,
  // Importing a module script failed — same root cause as dynamic import failures
  /Importing a module script failed/,
  // OAuth CSRF state mismatch — stale tab or browser back navigation, not actionable
  'Invalid state code',
];

const THIRD_PARTY_FRAME_PATTERNS = [
  /gtm\.js/,
  /googletagmanager\.com/,
  /google-analytics\.com/,
  /crisp\.chat/,
];

function hasThirdPartyFrames(event: ISentryEvent): boolean {
  const frames =
    event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];

  return frames.some((frame) => {
    const path = frame.abs_path ?? frame.filename ?? '';
    return THIRD_PARTY_FRAME_PATTERNS.some((pattern) => pattern.test(path));
  });
}

const NOISY_AXIOS_MESSAGES = [
  'Network Error',
  'Request aborted',
  'timeout exceeded',
  'Request failed with status code 404',
];

const NOISY_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS for',
];

/**
 * Belt-and-suspenders: drop noisy errors regardless of how Sentry
 * formats the error string (ignoreErrors matching can be unreliable).
 */
function isNoisyError(event: ISentryEvent): boolean {
  const firstException = event.exception?.values?.[0];
  if (!firstException?.value) return false;

  const value = firstException.value;

  if (firstException.type === 'AxiosError') {
    return NOISY_AXIOS_MESSAGES.some((msg) => value.includes(msg));
  }

  return NOISY_CHUNK_MESSAGES.some((msg) => value.includes(msg));
}

/**
 * Returns a Sentry `beforeSend` callback.
 *
 * Drops events when the user is offline. Pass an optional `extraFilter`
 * predicate for app-specific filtering — return `false` to drop the event.
 */
export function createSentryBeforeSend<T extends ISentryEvent>(
  extraFilter?: (event: T) => boolean
): (event: T) => T | null {
  return function beforeSend(event) {
    if (!navigator.onLine) {
      return null;
    }

    if (hasThirdPartyFrames(event) || isNoisyError(event)) {
      return null;
    }

    if (extraFilter && !extraFilter(event)) {
      return null;
    }
    return event;
  };
}
